import { LitElement, html, css, styleMap, utils } from '@hh/global.js'
const { isMobile } = utils

/**
 * scroll state -1: haven't scrolled yet
 * scroll state 0: initial scroll down
 * scroll state 1: scrolling down, past a certain point
 * scroll state 2: scrolling up
 */

class HHStickyHeader extends LitElement {
  static properties = {
    closeWhenHidden: {
      type: String,
      attribute: 'close-when-hidden',
    },
    _headerHeight: {
      type: Number,
      state: true,
    },
    _announcementHeight: {
      type: Number,
      state: true,
    },
    _state: {
      type: Number,
      state: true,
    },
    _height: {
      type: Number,
      state: true,
    },
  }

  constructor() {
    super()

    this.headerBounds = {}
    this.currentScrollTop = 0
    this.closeWhenHidden = 'hh-details-modal'

    this._state = -1
  }

  connectedCallback() {
    super.connectedCallback()
    this.onScrollHandler = this.onScroll.bind(this)
    this.setSpacerHandler = this.setSpacerHeight.bind(this)
    window.addEventListener('scroll', this.onScrollHandler, { passive: true })
    window.addEventListener('resize', this.setSpacerHandler)
    this.createObserver()
    setTimeout(() => {
      this.setSpacerHeight()
    }, 0)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('scroll', this.onScrollHandler)
    window.removeEventListener('resize', this.setSpacerHandler)
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect
      observer.disconnect()
    })

    observer.observe(this)
  }

  setSpacerHeight() {
    const headerHeight =
      this.querySelector('hh-header')?.getBoundingClientRect().height
    const announcementHeight =
      this.querySelector('#announcement-bar')?.getBoundingClientRect().height ||
      0
    this._headerHeight = headerHeight
    this._announcementHeight = announcementHeight

    document
      .querySelector(':root')
      .style.setProperty(`--hh-announcement-height`, `${announcementHeight}px`)
    document
      .querySelector(':root')
      .style.setProperty(`--hh-header-height`, `${headerHeight}px`)

    this._height = announcementHeight
    this._state = 0
    // ensures that the header doesn't overlap the content of <main>
    if (this.dataset.page != 'index' && this.dataset.page != '404') {
      document.querySelector('main').style.paddingTop = `${
        headerHeight + announcementHeight
      }px`
    }
  }

  broadcast(scrollTop) {
    this.querySelector('hh-header').dispatchEvent(
      new CustomEvent('stickyScrolled', {
        bubbles: true,
        composed: true,
        detail: {
          value: scrollTop,
        },
      })
    )
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop

    this.broadcast(scrollTop)

    if (
      scrollTop > this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      // scroll down
      requestAnimationFrame(this.hide.bind(this))
    } else if (
      scrollTop < this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      // scroll up
      requestAnimationFrame(this.reveal.bind(this))
    } else if (scrollTop <= this.headerBounds.top) {
      // at top
      requestAnimationFrame(this.reset.bind(this))
    }

    this.currentScrollTop = scrollTop
  }

  hide() {
    if (this._state === 1) {
      return
    }
    this._state = 1

    document
      .querySelector(':root')
      .style.setProperty(`--hh-announcement-height`, `0px`)

    utils.all(this.closeWhenHidden, this.header).map((element) => {
      element.setAttribute('open', false)
    })
  }

  reveal() {
    if (this._state === 2) {
      return
    }

    const footer = document.querySelector('footer')

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          document
            .querySelector(':root')
            .style.setProperty(
              `--hh-announcement-height`,
              `${this._announcementHeight}px`
            )
        }
      })
    }, {})

    // Tell the observer which elements to track
    observer.observe(footer)

    this._state = 2
  }

  reset() {
    if (this._state === 0) {
      return
    }

    this._state = 0
  }

  render() {
    return html`<div
      class="container"
      style="${styleMap({
        position: this._state === -1 ? 'relative' : 'fixed',
        transform:
          this._state === 1
            ? `translateY(-${this._height}px)`
            : 'translateY(0)',
      })}"
    >
      <slot></slot>
    </div> `
  }

  static styles = css`
    .container {
      left: 0;
      position: fixed;
      top: 0;
      width: 100%;
      z-index: var(--hh-z-modal, 100);
      transform: translateY(0);
      transition: transform var(--hh-duration-long, 500ms);
      background-color: var(--hh-background, transparent);
    }
  `
}

customElements.define('hh-sticky-header', HHStickyHeader)
